import { useState } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import {
  LayoutDashboard,
  BarChart3,
  Settings,
  Link2,
  Menu,
  X,
  LogOut,
  ChevronDown,
  Bot,
  BookOpen,
  Inbox,
  PanelLeftClose,
  PanelLeft,
  Zap,
  SlidersHorizontal,
  Shield,
  Users,
} from 'lucide-react'
import { TenantSwitcher } from '@/components/TenantSwitcher'


function getNavGroups(isSuperAdmin: boolean) {
  const groups = []

  if (isSuperAdmin) {
    groups.push({
      label: 'Administration',
      items: [
        { name: 'Admin Panel', href: '/admin', icon: Shield },
      ],
    })
  }

  groups.push(
    {
      label: 'Overview',
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Inbox', href: '/inbox', icon: Inbox },
        { name: 'Analytics', href: '/analytics', icon: BarChart3 },
      ],
    },
    {
      label: 'AI & Automation',
      items: [
        { name: 'AI Agents', href: '/agents', icon: Bot },
        ...(isSuperAdmin ? [
          { name: 'Knowledge Base', href: '/knowledge-base', icon: BookOpen },
          { name: 'AI Settings', href: '/ai-settings', icon: SlidersHorizontal },
        ] : []),
      ],
    },
    {
      label: 'System',
      items: [
        { name: 'Team', href: '/team', icon: Users },
        ...(isSuperAdmin ? [
          { name: 'Integrations', href: '/integrations', icon: Link2 },
        ] : []),
        { name: 'Settings', href: '/settings', icon: Settings },
      ],
    },
  )

  return groups
}

const pageTitle: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/inbox': 'Inbox',
  '/agents': 'AI Agents',
  '/knowledge-base': 'Knowledge Base',
  '/ai-settings': 'AI Settings',
  '/analytics': 'Analytics',
  '/team': 'Team',
  '/integrations': 'Integrations',
  '/settings': 'Settings',
  '/admin': 'Admin Panel',
}

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [manualCollapsed, setManualCollapsed] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const { user, logout, isSuperAdmin } = useAuth()
  const navGroups = getNavGroups(isSuperAdmin)
  const navigate = useNavigate()
  const location = useLocation()
  const isInboxRoute = location.pathname.startsWith('/inbox')
  const sidebarCollapsed = isInboxRoute || manualCollapsed

  const currentPageTitle = pageTitle[location.pathname] || 'ReplyForge'

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full sidebar-gradient transform transition-all duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${sidebarCollapsed ? 'w-16' : 'w-[260px]'}
      `}>
        {/* Logo */}
        <div className={`h-16 flex items-center justify-between ${sidebarCollapsed ? 'px-3' : 'px-5'} border-b border-gray-200/60`}>
          <div className="flex items-center space-x-3 min-w-0">
            <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-yellow-500/20">
              <Zap className="w-5 h-5 text-gray-900" strokeWidth={2.5} />
            </div>
            {!sidebarCollapsed && (
              <div className="flex flex-col">
                <span className="text-gray-900 font-bold text-base tracking-tight leading-none">ReplyForge</span>
                <span className="text-xs text-gray-400 font-medium tracking-widest uppercase mt-0.5">Platform</span>
              </div>
            )}
          </div>
          <button
            className="lg:hidden text-gray-400 hover:text-gray-700"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
          {/* Desktop collapse toggle */}
          <button
            className="hidden lg:flex text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1 rounded-lg transition-colors"
            onClick={() => setManualCollapsed(!manualCollapsed)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <PanelLeft className="w-4 h-4" />
            ) : (
              <PanelLeftClose className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className={`mt-4 flex-1 overflow-y-auto ${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
          {navGroups.map((group) => (
            <div key={group.label} className="mb-5">
              {!sidebarCollapsed && (
                <p className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-[0.12em]">
                  {group.label}
                </p>
              )}
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.name}>
                    <NavLink
                      to={item.href}
                      className={({ isActive }) => `
                        group flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'space-x-3 px-3'} py-2.5 rounded-lg transition-all duration-150 relative
                        ${isActive
                          ? 'bg-amber-50/80 text-amber-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                      onClick={() => setSidebarOpen(false)}
                      title={sidebarCollapsed ? item.name : undefined}
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-amber-500 rounded-r-full" />
                          )}
                          <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-amber-600' : ''}`} />
                          {!sidebarCollapsed && (
                            <span className="text-sm font-medium">{item.name}</span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* User section at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-200/60">
          {sidebarCollapsed ? (
            <div className="flex justify-center">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg shadow-yellow-500/10" title={user?.name || 'User'}>
                <span className="text-gray-900 font-bold text-sm">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-3 px-3 py-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-default">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-yellow-500/10">
                <span className="text-gray-900 font-bold text-sm">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email}
                </p>
              </div>
              <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" title="Online" />
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-[260px]'}`}>
        {/* Top header */}
        <header className="sticky top-0 z-30 h-14 bg-white/80 backdrop-blur-xl border-b border-gray-200/80 flex items-center justify-between px-4 lg:px-6">
          {/* Left side */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>

            {/* Page title */}
            <h1 className="hidden lg:block text-base font-semibold text-gray-900">
              {currentPageTitle}
            </h1>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-3">
            {isSuperAdmin && <TenantSwitcher />}
            {/* User menu */}
            <div className="relative">
              <button
                className="flex items-center space-x-2 p-1.5 rounded-xl hover:bg-gray-100 transition-colors"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-sm">
                  <span className="text-gray-900 font-bold text-xs">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl shadow-black/10 border border-gray-200/80 py-1 z-50 animate-dropdown">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{user?.name}</p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
                    </div>
                    <div className="py-1">
                      <NavLink
                        to="/settings"
                        className="flex items-center space-x-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4 text-gray-400" />
                        <span>Settings</span>
                      </NavLink>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign out</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className={isInboxRoute ? 'h-[calc(100vh-3.5rem)]' : 'p-4 lg:p-6'}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
